import React, { useState, FC } from 'react';
import {
  Box,
  Text,
  AvatarSize,
  Icon,
  IconSize,
  ToggleSwitch,
  ToastAction,
  ToastType,
  useToastsDispatch,
  Spinner,
  SpinnerSize,
  AvatarGroup,
  AvatarGroupSpacing,
} from '@bechtel/gds';
import { FillerMaterialBoxStyled } from './styled';
import { Trans, t } from '@lingui/macro';
import { get, map, noop, some } from 'lodash';
import {
  CrewMember,
  DeleteCaddyInput,
  DeleteCaddyPayload,
  RodRequestCaddy,
  Caddy,
  RodRequestCaddyStatusCode,
  UpdateCaddyInput,
  UpdateCaddyPayload,
} from '@bechtel/types';
import { EditCaddy } from '../../../edit';
import { UPDATE_CADDY, DELETE_CADDY } from '@bechtel/client-gql';
import { useMutation } from '@apollo/client';
import { MutationError } from '../../error/types';
import ErrorModal from '../../error';
import { ErrorBoundary, useDate, RestrictTo, useName } from '@bechtel/client-common';
import { parseISO } from 'date-fns';
import { DeleteRodRequestModal } from '~/components/atoms/confirm-delete';
import { useRodRoomPermissions } from '@bechtel/client-weld-common';

interface CaddyDetailItemProps {
  caddy: RodRequestCaddy;
  welders: CrewMember[];
  returnCaddies: Caddy[];
  handleReturnCaddies: (caddies: Caddy[]) => void;
}

const ToastContent = (textContent) => {
  return (
    <Box pb={{ sm: '550' }} mb={{ sm: '400' }} css={{ borderBottom: '1px solid rgba(255, 255, 255, 0.22)' }}>
      <Text family="graphik" weight="400" size="500">
        <Trans>{textContent}</Trans>
      </Text>
    </Box>
  );
};

export const CaddyDetailItem: FC<CaddyDetailItemProps> = ({ caddy, welders, returnCaddies, handleReturnCaddies }) => {
  const { canReturnCaddies, canUpdateCaddy, canDeleteCaddy } = useRodRoomPermissions();
  const [isFillerMaterialListOpen, setIsFillerMaterialListOpen] = useState(false);
  const [showEditSidesheet, setShowEditSidesheet] = useState(false);
  const [updateCaddyError, setUpdateCaddyError] = useState<MutationError | null>(null);
  const [hovered, setHovered] = useState(false);
  const [deleteCaddyError, setDeleteCaddyError] = useState<MutationError | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const dispatchToast = useToastsDispatch();
  const { formatRelativeInWords } = useDate();
  const { getFullName } = useName();
  const [onUpdateCaddy, { loading }] = useMutation<{ updateCaddy: UpdateCaddyPayload }, { input: UpdateCaddyInput }>(
    UPDATE_CADDY,
    {
      onCompleted: (data) => {
        const updateCaddyError = get(data, 'updateCaddy.error');
        if (updateCaddyError) {
          setUpdateCaddyError(updateCaddyError);
        } else {
          setShowEditSidesheet(false);
          openUpdateCaddyToast();
        }
      },
    }
  );

  const openUpdateCaddyToast = () => {
    const createAddCaddyToast: ToastAction = {
      type: 'ADD_TOAST',
      payload: {
        toastType: ToastType.INFO,
        content: () => ToastContent(t`Caddy updated successfully.`),
        autoDismissTimeout: 7000,
        alertAction: {
          label: t`Got It`,
          onClickHandler: () => noop,
        },
      },
    };
    dispatchToast(createAddCaddyToast);
  };

  const closeModal = () => {
    setUpdateCaddyError(null);
    setDeleteCaddyError(null);
  };

  const [deleteCaddy, { loading: deleteLoading }] = useMutation<
    { deleteCaddy: DeleteCaddyPayload },
    { input: DeleteCaddyInput }
  >(DELETE_CADDY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const deleteCaddyError = get(data, 'deleteCaddy.error');
      setShowDeleteConfirm(false);
      if (deleteCaddyError) {
        setDeleteCaddyError(deleteCaddyError);
      } else {
        setDeleteCaddyError(null);
        const toastAction: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: () => ToastContent(t`Caddy Succesfully Deleted.`),
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(toastAction);
      }
    },
    onError: (error) => {
      setDeleteCaddyError(error);
    },
  });

  const onDeleteCaddy = () => {
    deleteCaddy({
      variables: {
        input: {
          id: caddy.id,
        },
      },
    });
  };

  const handleCaddyUpdate = (updatedCaddy) => {
    onUpdateCaddy({
      variables: {
        input: {
          caddyId: updatedCaddy.caddy.id,
          fillerMaterialDiameterDetailId: map(updatedCaddy.rods, (rod) => rod.heatLotNumber.id),
          fillerMaterialId: updatedCaddy.fillerMaterial.id,
          issuedToStencilIds: updatedCaddy?.issuedTo?.length ? map(updatedCaddy.issuedTo, (item) => item.id) : [],
          id: caddy.id,
          pickedUpBy: updatedCaddy.pickedBy.id,
        },
      },
    });
  };

  const stencils = map(caddy.stencils, (stencil: CrewMember) => {
    return {
      src: stencil.imageUrl ? stencil.imageUrl : '',
      alt: getFullName(stencil?.firstName ? stencil.firstName : ' ', stencil?.lastName ? stencil.lastName : ' '),
    };
  });

  const isCaddyReturned: boolean = some(returnCaddies, (returnCaddy) => returnCaddy?.id === caddy?.caddy?.id);

  const handleToggleSwitch = () => {
    if (returnCaddies.length) {
      handleReturnCaddies([]);
    } else {
      handleReturnCaddies([...returnCaddies, caddy.caddy]);
    }
  };

  return (
    <>
      <Box
        mt={{ sm: '200' }}
        mb={{ sm: '400' }}
        css={`
          ${({ theme }) =>
            `background: ${theme.colors.white}; border-radius: ${theme.spacings[200]}; box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);`};
        `}
        flexDirection={{ sm: 'column' }}
      >
        <Box flexGrow={{ sm: 1 }} flexDirection={{ sm: 'column' }} py={{ sm: '300' }} px={{ sm: '450' }}>
          <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'space-between' }}>
            <Text
              size="400"
              weight="600"
              color="grey.600"
              onClick={() => setIsFillerMaterialListOpen(!isFillerMaterialListOpen)}
            >
              {caddy.caddy?.label} &nbsp;
              <Icon
                icon={!isFillerMaterialListOpen ? 'chevron-right' : 'chevron-down'}
                fillColor="bechtel.blue.700"
                size={IconSize.SMALL}
                iconStyle="duo"
                style={{ cursor: 'pointer' }}
              />
            </Text>
            <Box alignSelf={{ sm: 'flex-end' }} pr={{ sm: '450' }} pb={{ sm: '400' }} style={{ minHeight: '40px' }}>
              <AvatarGroup
                maxCount={caddy.stencils?.length}
                data={stencils}
                size={AvatarSize.MEDIUM}
                spacing={AvatarGroupSpacing.MEDIUM}
              />
            </Box>
          </Box>
          <Text size="300" weight="400" color="grey.600">
            <Trans>Issued</Trans>&nbsp;
            {formatRelativeInWords(parseISO(caddy?.issuedAt), new Date(), RestrictTo.Day, 'HH:mm aa', ``, true)}
          </Text>
        </Box>

        <Box px={{ sm: '450' }} flexDirection={{ sm: 'column' }}>
          {isFillerMaterialListOpen &&
            map(caddy?.fillerMaterial?.diameters, (item, index) => (
              <FillerMaterialBoxStyled key={index}>
                <Box px={{ sm: '400' }} py={{ sm: '300' }} flexDirection={{ sm: 'column' }} css={{ lineHeight: 1.25 }}>
                  <Text size="300">{caddy?.fillerMaterial?.fillerMaterial?.label}</Text>
                  <Text size="200" color="grey.300">
                    {item?.heatLotNumber?.label}
                  </Text>
                </Box>
                <Text size="300">{item?.diameter?.label}</Text>
              </FillerMaterialBoxStyled>
            ))}
        </Box>
        <Box
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'space-between' }}
          flexGrow={{ sm: 1 }}
          css={`
            ${({ theme }) => `background: ${theme.colors.grey[100]}; min-height: 28px;`}
          `}
          px={{ sm: '450' }}
          py={{ sm: '300' }}
          mt={{ sm: '400' }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {canReturnCaddies && (
            <Box alignItems={{ sm: 'center' }}>
              {/* ToDo Return all should toggle as per all child toggle switches */}
              <ToggleSwitch
                isToggled={isCaddyReturned || caddy.currentStatusI18n === RodRequestCaddyStatusCode.RETURNED}
                onToggle={handleToggleSwitch}
                disabled={caddy.currentStatusI18n === RodRequestCaddyStatusCode.RETURNED}
              />
              &nbsp;&nbsp;
              <Text size="300" color="bechtel.blue.700" weight="400">
                <Trans>Return</Trans>
              </Text>
            </Box>
          )}
          {caddy.currentStatusI18n === RodRequestCaddyStatusCode.RETURNED && (
            <>
              <Text size="300" color="grey.600" weight="400">
                <Trans>at</Trans>&nbsp;
                {formatRelativeInWords(parseISO(caddy.issuedAt), new Date(), RestrictTo.Day, 'HH:mm aa', ``, true)}
              </Text>
            </>
          )}
          {caddy.currentStatusI18n !== RodRequestCaddyStatusCode.RETURNED &&
            hovered &&
            (deleteLoading ? (
              <Spinner size={SpinnerSize.SMALL} />
            ) : (
              <>
                {(canUpdateCaddy || canDeleteCaddy) && (
                  <Box>
                    {canUpdateCaddy && (
                      <Icon
                        icon="edit"
                        size={IconSize.SMALL}
                        fillColor="bechtel.blue.700"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setShowEditSidesheet(true)}
                      />
                    )}
                    &nbsp;&nbsp;
                    {canDeleteCaddy && (
                      <Icon
                        icon="trash-can"
                        size={IconSize.SMALL}
                        fillColor="red.500"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setShowDeleteConfirm(true)}
                      />
                    )}
                  </Box>
                )}
              </>
            ))}
        </Box>
      </Box>
      {showEditSidesheet && (
        <ErrorBoundary>
          <EditCaddy
            caddy={{
              caddy: caddy.caddy,
              fillerMaterial: caddy.fillerMaterial,
              stencils: caddy.stencils,
              pickedUpBy: caddy.pickedUpBy,
            }}
            onClose={() => {
              setShowEditSidesheet(false);
            }}
            onUpdateCaddy={(updatedCaddy) => handleCaddyUpdate(updatedCaddy)}
            welders={welders}
            loading={loading}
          />
        </ErrorBoundary>
      )}
      {(updateCaddyError || deleteCaddyError) && (
        <ErrorBoundary>
          <ErrorModal caddyError={updateCaddyError || deleteCaddyError} close={closeModal} />
        </ErrorBoundary>
      )}
      {showDeleteConfirm && (
        <DeleteRodRequestModal
          onDelete={() => onDeleteCaddy()}
          onCancel={() => setShowDeleteConfirm(false)}
          title={'Delete Caddy'}
          textContent={`Are you sure you want to delete the Caddy ${caddy.caddy.label} ?`}
        ></DeleteRodRequestModal>
      )}
    </>
  );
};
